/**
 * Frontend permission utilities for Role-Based Access Control (RBAC)
 * 
 * This module provides utilities for checking permissions in the frontend
 * based on user roles and the permission system defined in the backend.
 */

import { UserRole } from '@/types';

/**
 * Available permissions in the system (matching backend)
 */
export enum Permission {
  // Planning Period permissions
  VIEW_PLANNING_PERIODS = 'view:planning_periods',
  CREATE_PLANNING_PERIODS = 'create:planning_periods',
  UPDATE_PLANNING_PERIODS = 'update:planning_periods',
  DELETE_PLANNING_PERIODS = 'delete:planning_periods',
  ACTIVATE_PLANNING_PERIODS = 'activate:planning_periods',
  
  // Investible Structure permissions
  VIEW_INVESTIBLE_STRUCTURES = 'view:investible_structures',
  CREATE_INVESTIBLE_STRUCTURES = 'create:investible_structures',
  UPDATE_INVESTIBLE_STRUCTURES = 'update:investible_structures',
  DELETE_INVESTIBLE_STRUCTURES = 'delete:investible_structures',
  UPDATE_OWN_INVESTIBLE_STRUCTURES = 'update:own_investible_structures',
  
  // EPIC permissions
  VIEW_EPICS = 'view:epics',
  CREATE_EPICS = 'create:epics',
  UPDATE_EPICS = 'update:epics',
  DELETE_EPICS = 'delete:epics',
  UPDATE_OWN_EPICS = 'update:own_epics',
  APPROVE_EPICS = 'approve:epics',
  
  // Feature permissions
  VIEW_FEATURES = 'view:features',
  CREATE_FEATURES = 'create:features',
  UPDATE_FEATURES = 'update:features',
  DELETE_FEATURES = 'delete:features',
  UPDATE_OWN_FEATURES = 'update:own_features',
  
  // Program permissions
  VIEW_PROGRAMS = 'view:programs',
  CREATE_PROGRAMS = 'create:programs',
  UPDATE_PROGRAMS = 'update:programs',
  DELETE_PROGRAMS = 'delete:programs',
  UPDATE_OWN_PROGRAMS = 'update:own_programs',
  
  // Team permissions
  VIEW_TEAMS = 'view:teams',
  CREATE_TEAMS = 'create:teams',
  UPDATE_TEAMS = 'update:teams',
  DELETE_TEAMS = 'delete:teams',
  
  // Allocation permissions
  VIEW_ALLOCATIONS = 'view:allocations',
  CREATE_ALLOCATIONS = 'create:allocations',
  UPDATE_ALLOCATIONS = 'update:allocations',
  DELETE_ALLOCATIONS = 'delete:allocations',
  UPDATE_OWN_ALLOCATIONS = 'update:own_allocations',
  
  // Cost Profile permissions
  VIEW_COST_PROFILES = 'view:cost_profiles',
  CREATE_COST_PROFILES = 'create:cost_profiles',
  UPDATE_COST_PROFILES = 'update:cost_profiles',
  DELETE_COST_PROFILES = 'delete:cost_profiles',

  // Rate Card permissions (Finance/Admin only)
  VIEW_RATE_CARDS = 'view:rate_cards',
  MANAGE_RATE_CARDS = 'manage:rate_cards',
  
  // Scenario permissions
  READ_SCENARIOS = 'read:scenarios',
  VIEW_SCENARIOS = 'view:scenarios',
  CREATE_SCENARIOS = 'create:scenarios',
  UPDATE_SCENARIOS = 'update:scenarios',
  DELETE_SCENARIOS = 'delete:scenarios',
  
  // Dependency permissions
  VIEW_DEPENDENCIES = 'view:dependencies',
  CREATE_DEPENDENCIES = 'create:dependencies',
  UPDATE_DEPENDENCIES = 'update:dependencies',
  DELETE_DEPENDENCIES = 'delete:dependencies',
  
  // Capacity permissions
  VIEW_CAPACITY = 'view:capacity',
  MANAGE_CAPACITY = 'manage:capacity',
  
  // Notification permissions
  CREATE_NOTIFICATIONS = 'create:notifications',
  
  // Dashboard permissions
  VIEW_EXECUTIVE_DASHBOARD = 'view:executive_dashboard',
  VIEW_IS_OWNER_DASHBOARD = 'view:is_owner_dashboard',
  VIEW_PROGRAM_LEAD_DASHBOARD = 'view:program_lead_dashboard',
  
  // User management permissions
  VIEW_USERS = 'view:users',
  MANAGE_USERS = 'manage:users',
  
  // Reporting permissions
  VIEW_REPORTS = 'view:reports',
  EXPORT_DATA = 'export:data',
  
  // Audit permissions
  VIEW_AUDIT_LOGS = 'view:audit_logs',
}

/**
 * Role-to-Permission mapping (matching backend)
 */
export const RolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: Object.values(Permission),

  [UserRole.EXECUTIVE]: [
    Permission.VIEW_PLANNING_PERIODS,
    Permission.ACTIVATE_PLANNING_PERIODS,
    Permission.VIEW_INVESTIBLE_STRUCTURES,
    Permission.VIEW_EPICS,
    Permission.APPROVE_EPICS,
    Permission.VIEW_FEATURES,
    Permission.VIEW_PROGRAMS,
    Permission.VIEW_TEAMS,
    Permission.VIEW_ALLOCATIONS,
    Permission.VIEW_COST_PROFILES,
    Permission.READ_SCENARIOS,
    Permission.VIEW_SCENARIOS,
    Permission.CREATE_SCENARIOS,
    Permission.VIEW_DEPENDENCIES,
    Permission.VIEW_CAPACITY,
    Permission.VIEW_EXECUTIVE_DASHBOARD,
    Permission.VIEW_IS_OWNER_DASHBOARD,
    Permission.VIEW_PROGRAM_LEAD_DASHBOARD,
    Permission.VIEW_REPORTS,
    Permission.EXPORT_DATA,
    Permission.VIEW_AUDIT_LOGS,
  ],

  [UserRole.IS_OWNER]: [
    Permission.VIEW_PLANNING_PERIODS,
    Permission.VIEW_INVESTIBLE_STRUCTURES,
    Permission.UPDATE_OWN_INVESTIBLE_STRUCTURES,
    Permission.VIEW_EPICS,
    Permission.CREATE_EPICS,
    Permission.UPDATE_OWN_EPICS,
    Permission.DELETE_EPICS,
    Permission.VIEW_FEATURES,
    Permission.CREATE_FEATURES,
    Permission.UPDATE_OWN_FEATURES,
    Permission.DELETE_FEATURES,
    Permission.VIEW_PROGRAMS,
    Permission.VIEW_TEAMS,
    Permission.VIEW_ALLOCATIONS,
    Permission.VIEW_COST_PROFILES,
    Permission.READ_SCENARIOS,
    Permission.VIEW_SCENARIOS,
    Permission.VIEW_DEPENDENCIES,
    Permission.CREATE_DEPENDENCIES,
    Permission.UPDATE_DEPENDENCIES,
    Permission.VIEW_CAPACITY,
    Permission.VIEW_IS_OWNER_DASHBOARD,
    Permission.VIEW_REPORTS,
  ],

  [UserRole.PROGRAM_LEAD]: [
    Permission.VIEW_PLANNING_PERIODS,
    Permission.VIEW_INVESTIBLE_STRUCTURES,
    Permission.VIEW_EPICS,
    Permission.UPDATE_EPICS,
    Permission.VIEW_FEATURES,
    Permission.UPDATE_FEATURES,
    Permission.VIEW_PROGRAMS,
    Permission.UPDATE_OWN_PROGRAMS,
    Permission.VIEW_TEAMS,
    Permission.UPDATE_TEAMS,
    Permission.VIEW_ALLOCATIONS,
    Permission.CREATE_ALLOCATIONS,
    Permission.UPDATE_OWN_ALLOCATIONS,
    Permission.DELETE_ALLOCATIONS,
    Permission.VIEW_COST_PROFILES,
    Permission.READ_SCENARIOS,
    Permission.VIEW_SCENARIOS,
    Permission.CREATE_SCENARIOS,
    Permission.VIEW_DEPENDENCIES,
    Permission.CREATE_DEPENDENCIES,
    Permission.UPDATE_DEPENDENCIES,
    Permission.VIEW_CAPACITY,
    Permission.VIEW_PROGRAM_LEAD_DASHBOARD,
    Permission.VIEW_REPORTS,
  ],

  [UserRole.PMO]: [
    Permission.VIEW_PLANNING_PERIODS,
    Permission.CREATE_PLANNING_PERIODS,
    Permission.UPDATE_PLANNING_PERIODS,
    Permission.DELETE_PLANNING_PERIODS,
    Permission.ACTIVATE_PLANNING_PERIODS,
    Permission.VIEW_INVESTIBLE_STRUCTURES,
    Permission.CREATE_INVESTIBLE_STRUCTURES,
    Permission.UPDATE_INVESTIBLE_STRUCTURES,
    Permission.DELETE_INVESTIBLE_STRUCTURES,
    Permission.VIEW_EPICS,
    Permission.CREATE_EPICS,
    Permission.UPDATE_EPICS,
    Permission.DELETE_EPICS,
    Permission.VIEW_FEATURES,
    Permission.CREATE_FEATURES,
    Permission.UPDATE_FEATURES,
    Permission.DELETE_FEATURES,
    Permission.VIEW_PROGRAMS,
    Permission.CREATE_PROGRAMS,
    Permission.UPDATE_PROGRAMS,
    Permission.DELETE_PROGRAMS,
    Permission.VIEW_TEAMS,
    Permission.CREATE_TEAMS,
    Permission.UPDATE_TEAMS,
    Permission.DELETE_TEAMS,
    Permission.VIEW_ALLOCATIONS,
    Permission.CREATE_ALLOCATIONS,
    Permission.UPDATE_ALLOCATIONS,
    Permission.DELETE_ALLOCATIONS,
    Permission.VIEW_COST_PROFILES,
    Permission.READ_SCENARIOS,
    Permission.VIEW_SCENARIOS,
    Permission.CREATE_SCENARIOS,
    Permission.UPDATE_SCENARIOS,
    Permission.DELETE_SCENARIOS,
    Permission.VIEW_DEPENDENCIES,
    Permission.CREATE_DEPENDENCIES,
    Permission.UPDATE_DEPENDENCIES,
    Permission.DELETE_DEPENDENCIES,
    Permission.VIEW_CAPACITY,
    Permission.MANAGE_CAPACITY,
    Permission.CREATE_NOTIFICATIONS,
    Permission.VIEW_EXECUTIVE_DASHBOARD,
    Permission.VIEW_IS_OWNER_DASHBOARD,
    Permission.VIEW_PROGRAM_LEAD_DASHBOARD,
    Permission.VIEW_REPORTS,
    Permission.EXPORT_DATA,
    Permission.VIEW_USERS,
    Permission.MANAGE_USERS,
    Permission.VIEW_AUDIT_LOGS,
  ],

  [UserRole.FINANCE]: [
    Permission.VIEW_PLANNING_PERIODS,
    Permission.VIEW_INVESTIBLE_STRUCTURES,
    Permission.VIEW_EPICS,
    Permission.VIEW_FEATURES,
    Permission.VIEW_PROGRAMS,
    Permission.VIEW_TEAMS,
    Permission.VIEW_ALLOCATIONS,
    Permission.VIEW_COST_PROFILES,
    Permission.CREATE_COST_PROFILES,
    Permission.UPDATE_COST_PROFILES,
    Permission.DELETE_COST_PROFILES,
    Permission.VIEW_RATE_CARDS,
    Permission.MANAGE_RATE_CARDS,
    Permission.READ_SCENARIOS,
    Permission.VIEW_SCENARIOS,
    Permission.VIEW_DEPENDENCIES,
    Permission.VIEW_CAPACITY,
    Permission.VIEW_EXECUTIVE_DASHBOARD,
    Permission.VIEW_REPORTS,
    Permission.EXPORT_DATA,
  ],

  [UserRole.VIEWER]: [
    Permission.VIEW_PLANNING_PERIODS,
    Permission.VIEW_INVESTIBLE_STRUCTURES,
    Permission.VIEW_EPICS,
    Permission.VIEW_FEATURES,
    Permission.VIEW_PROGRAMS,
    Permission.VIEW_TEAMS,
    Permission.VIEW_ALLOCATIONS,
    Permission.VIEW_COST_PROFILES,
    Permission.READ_SCENARIOS,
    Permission.VIEW_SCENARIOS,
    Permission.VIEW_DEPENDENCIES,
    Permission.VIEW_CAPACITY,
    Permission.VIEW_REPORTS,
  ],
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  return RolePermissions[role]?.includes(permission) ?? false;
}

/**
 * Check if a role has any of the specified permissions
 */
export function hasAnyPermission(role: UserRole, permissions: Permission[]): boolean {
  return permissions.some(permission => hasPermission(role, permission));
}

/**
 * Check if a role has all of the specified permissions
 */
export function hasAllPermissions(role: UserRole, permissions: Permission[]): boolean {
  return permissions.every(permission => hasPermission(role, permission));
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(role: UserRole): Permission[] {
  return RolePermissions[role] ?? [];
}